// src/pages/lead/LeadLeave.jsx
// Team Lead Leave Management: Team requests + My leaves

import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useMyLeaveRequests,
  submitLeaveRequest,
  useAllUsers,
  getTodayStr,
} from "../../hooks/useFirestore";
import { useLeadData } from "../../context/LeadContext";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

export default function LeadLeave() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid;

  const {
    leaves: teamRequests,
    reviewLeaveRequest,
  } = useLeadData();

  const { data: myRequests } = useMyLeaveRequests(uid);
  const { data: users } = useAllUsers();

  const today = getTodayStr();

  const [form, setForm] = useState({
    leaveType: "casual",
    fromDate: "",
    toDate: "",
    reason: "",
    handoverTo: "",
  });

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

   const [totalDays, setTotalDays] = useState(0);

  const [reviewing, setReviewing] = useState({});
function handleChange(e) {
  const { name, value } = e.target;

  setForm((prev) => {
    const updated = {
      ...prev,
      [name]: value,
    };

    if (updated.fromDate && updated.toDate) {
      const from = new Date(updated.fromDate);
      const to = new Date(updated.toDate);

      let days = 0;
      let current = new Date(from);

      while (current <= to) {
        if (current.getDay() !== 0) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }

      setTotalDays(days);
    } else {
      setTotalDays(0);
    }

    return updated;
  });
}

  async function handleSubmit(e) {
    e.preventDefault();

    setMsg("");

    if (form.fromDate < today) {
      setMsg("❌ Leave start date cannot be in the past.");
      return;
    }

    if (form.toDate < form.fromDate) {
      setMsg("❌ End date must be on or after the start date.");
      return;
    }

    const leaveBalance =
      userProfile?.leaveBalance?.[form.leaveType] || 0;

    // Calculate requested leave days (excluding Sundays)
    const from = new Date(form.fromDate);
    const to = new Date(form.toDate);

    let totalDays = 0;
    let curr = new Date(from);
    while (curr <= to) {
      if (curr.getDay() !== 0) {
        totalDays++;
      }
      curr.setDate(curr.getDate() + 1);
    }

    if (totalDays > leaveBalance) {
      alert("A leave request already exists for the selected date range.");
      return;
    }

    setSaving(true);

    const duplicateLeave = myRequests.some(
  (leave) =>
    leave.fromDate === form.fromDate &&
    leave.toDate === form.toDate &&
    leave.status !== "rejected"
);

if (duplicateLeave) {
        alert("A leave request already exists for the selected date range.");

  return;
}

    try {
      const handoverUser = users.find(
        (u) => u.uid === form.handoverTo
      );

      await submitLeaveRequest(userProfile, {
        ...form,
        handoverToName:
          handoverUser?.displayName || "",
      });

      setMsg("✅ Leave request submitted!");

      setForm({
        leaveType: "casual",
        fromDate: "",
        toDate: "",
        reason: "",
        handoverTo: "",
      });
    } catch (err) {
      setMsg("❌ " + err.message);
    }

    setSaving(false);
  }

  async function handleReview(requestId, action) {
    if (reviewing[requestId]) return;

    setReviewing((p) => ({
      ...p,
      [requestId]: "loading",
    }));

    try {
      await reviewLeaveRequest(
        requestId,
        action,
        uid
      );

      setReviewing((p) => ({
        ...p,
        [requestId]: "done",
      }));

      setMsg(
        `✅ Leave request ${
          action === "approve"
            ? "approved"
            : "rejected"
        } successfully.`
      );
    } catch (err) {
      setMsg("❌ " + err.message);

      setReviewing((p) => ({
        ...p,
        [requestId]: false,
      }));
    }
  }

  const myTeam = users.filter(
    (u) =>
      u.teamId === userProfile?.teamId &&
      u.uid !== uid
  );

  const bal = userProfile?.leaveBalance || {};
  const ent =
    userProfile?.leaveEntitlement || {};

  const pendingTeamRequests =
    teamRequests.filter(
      (r) => r.status === "pending_lead"
    );

  return (
    <Layout
      navItems={LEAD_NAV}
      panelLabel="Team Lead Panel"
    >
      <div className="page-header">
        <h1 className="page-title">
          Leave Management
        </h1>
      </div>

      {/* Leave Balance Cards */}
      <div className="leave-balance-row">
        {[
          {
            type: "casual",
            label: "Casual Leave",
            color: "blue",
          },
          {
            type: "sick",
            label: "Sick Leave",
            color: "green",
          },
          {
            type: "annual",
            label: "Annual Leave",
            color: "amber",
          },
        ].map(({ type, label, color }) => (
          <div
            key={type}
            className={`leave-balance-card leave-balance-card--${color}`}
          >
            <span className="lbc-label">
              {label}
            </span>

            <span className="lbc-balance">
              {bal[type] ?? 0}
            </span>

            <span className="lbc-sub">
              {(ent[type] || 0) -
                (bal[type] || 0)}{" "}
              used of {ent[type] || 0}
            </span>

            <div className="lbc-bar">
              <div
                className="lbc-fill"
                style={{
                  width:
                    ent[type] > 0
                      ? `${Math.min(
                          Math.round(
                            (((ent[type] || 0) -
                              (bal[type] || 0)) /
                              ent[type]) *
                              100
                          ),
                          100
                        )}%`
                      : "0%",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {msg && (
        <div
          className={
            msg.startsWith("✅")
              ? "form-success"
              : "form-error"
          }
          style={{
            marginBottom: "1rem",
          }}
        >
          {msg}
        </div>
      )}

      <div
        className="two-col-layout"
        style={{
          alignItems: "flex-start",
        }}
      >
        {/* LEFT SIDE */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            position: "sticky",
            top: "20px",
            alignSelf: "flex-start",
          }}
        >
          {/* Apply Leave Form */}
          <div className="form-card">
            <h3 className="form-card-title">
              Apply for Leave
            </h3>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Leave Type *</label>

                <select
                  name="leaveType"
                  value={form.leaveType}
                  onChange={handleChange}
                >
                  <option value="casual">
                    Casual Leave
                  </option>

                  <option value="sick">
                    Sick Leave
                  </option>

                  <option value="annual">
                    Annual Leave
                  </option>
                </select>
              </div>

             <div className="form-row">
  <div className="form-group">
    <label>From *</label>
    <input
      name="fromDate"
      type="date"
      value={form.fromDate}
      onChange={handleChange}
      required
      min={today}
    />
  </div>

  <div className="form-group">
    <label>To *</label>
    <input
      name="toDate"
      type="date"
      value={form.toDate}
      onChange={handleChange}
      required
      min={form.fromDate || today}
    />
  </div>

  <div className="form-group">
    <label>Days</label>
    <input
      type="text"
      value={totalDays > 0 ? `${totalDays} Day${totalDays > 1 ? "s" : ""}` : ""}
      readOnly
    />
  </div>
</div>
              <div className="form-group">
                <label>Reason *</label>

                <textarea
                  name="reason"
                  value={form.reason}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="Reason for leave…"
                />
              </div>

              <div className="form-group">
                <label>Handover To</label>

                <select
                  name="handoverTo"
                  value={form.handoverTo}
                  onChange={handleChange}
                >
                  <option value="">
                    — Select colleague —
                  </option>

                  {myTeam.map((u) => (
                    <option
                      key={u.uid}
                      value={u.uid}
                    >
                      {u.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving
                  ? "Submitting…"
                  : "Submit Request"}
              </button>
            </form>
          </div>

          {/* Leave History */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title">
                Leave History
              </h3>
            </div>

            {myRequests.length === 0 ? (
              <div className="card-empty">
                No leave requests yet.
              </div>
            ) : (
              <div className="leave-history-list">
                {myRequests.map((r) => (
                  <div
                    key={r.id}
                    className="leave-history-item"
                  >
                    <div className="leave-history-header">
                      <span
                        className="leave-history-type"
                        style={{
                          textTransform:
                            "capitalize",
                        }}
                      >
                        {r.leaveType} Leave
                      </span>

                      <span
                        className={`status-pill status-pill--${
                          r.status ===
                          "approved"
                            ? "green"
                            : r.status ===
                              "rejected"
                            ? "red"
                            : "amber"
                        }`}
                      >
                        {r.status ===
                        "pending_lead"
                          ? "Pending Lead"
                          : r.status ===
                            "pending_admin"
                          ? "Pending Admin"
                          : r.status}
                      </span>
                    </div>

                    <div
                      className="leave-history-dates"
                      style={{
                        display: "flex",
                        alignItems:
                          "center",
                        gap: "6px",
                        flexWrap: "wrap",
                      }}
                    >
                      📅 {r.fromDate} →{" "}
                      {r.toDate} ·{" "}
                      {r.totalDays} day
                      {r.totalDays > 1
                        ? "s"
                        : ""}
                    </div>

                    <div className="leave-history-reason">
                      {r.reason}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">
              Team Leave Requests
            </h3>

            <span className="status-pill status-pill--amber">
              {pendingTeamRequests.length}{" "}
              pending
            </span>
          </div>

          {pendingTeamRequests.length ===
          0 ? (
            <div className="card-empty">
              No pending leave requests
              from your team.
            </div>
          ) : (
            <div className="leave-requests-list">
              {pendingTeamRequests.map(
                (r) => {
                  const isLoading =
                    reviewing[r.id] ===
                    "loading";

                  const isDone =
                    reviewing[r.id] ===
                    "done";

                  const isDisabled =
                    isLoading || isDone;

                  return (
                    <div
                      key={r.id}
                      className="leave-request-item"
                    >
                      <div className="leave-request-header">
                        <span className="leave-request-name">
                          {r.displayName}
                        </span>

                        <span className="status-pill status-pill--amber">
                          Pending Review
                        </span>
                      </div>

                      <div className="leave-request-type">
                        {r.leaveType
                          .charAt(0)
                          .toUpperCase() +
                          r.leaveType.slice(
                            1
                          )}{" "}
                        Leave
                      </div>

                      <div className="leave-request-dates">
                        📅 {r.fromDate} →{" "}
                        {r.toDate} (
                        {r.totalDays} day
                        {r.totalDays > 1
                          ? "s"
                          : ""}
                        )
                      </div>

                      <div className="leave-request-reason">
                        {r.reason}
                      </div>

                      <div className="leave-request-actions">
                        <button
                          onClick={() =>
                            handleReview(
                              r.id,
                              "approve"
                            )
                          }
                          disabled={
                            isDisabled
                          }
                          className="btn-success-sm"
                        >
                          {isLoading
                            ? "Processing…"
                            : isDone
                            ? "Done"
                            : "Approve"}
                        </button>

                        <button
                          onClick={() =>
                            handleReview(
                              r.id,
                              "reject"
                            )
                          }
                          disabled={
                            isDisabled
                          }
                          className="btn-danger-sm"
                        >
                          {isLoading
                            ? "Processing…"
                            : isDone
                            ? "Done"
                            : "Reject"}
                        </button>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}